import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { bookingServiceAPI, Booking } from '../services/bookingServiceAPI';
import '../styles/AdminCalendar.css';

function AdminCalendar() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    const token = localStorage.getItem('fotomista_token');
    if (!token) {
      navigate('/admin-login');
      return;
    }
    fetchBookings();
  }, [navigate]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const confirmedBookings = await bookingServiceAPI.getConfirmedBookings();
      setBookings(confirmedBookings);
    } catch (error) {
      console.error('Error fetching bookings for calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // إيجاد الحجز القادم
  const getUpcomingBooking = () => {
    const now = new Date();
    // تصفية الحجوزات المستقبلية فقط و ترتيبها
    const upcoming = bookings
      .filter(b => {
        const eventDate = new Date(b.eventDate);
        if (b.eventTime) {
          const [hours, minutes] = b.eventTime.split(':').map(Number);
          eventDate.setHours(hours, minutes, 0, 0);
        }
        return eventDate > now;
      })
      .sort((a, b) => {
        const dateA = new Date(a.eventDate);
        if (a.eventTime) {
          const [hA, mA] = a.eventTime.split(':').map(Number);
          dateA.setHours(hA, mA, 0, 0);
        }
        const dateB = new Date(b.eventDate);
        if (b.eventTime) {
          const [hB, mB] = b.eventTime.split(':').map(Number);
          dateB.setHours(hB, mB, 0, 0);
        }
        return dateA.getTime() - dateB.getTime();
      });

    return upcoming.length > 0 ? upcoming[0] : null;
  };

  const upcomingBooking = getUpcomingBooking();

  // منطق التقويم
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year: number, month: number) => {
    return new Date(year, month, 1).getDay(); // 0 for Sunday
  };

  const daysInMonth = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
  const firstDay = getFirstDayOfMonth(currentDate.getFullYear(), currentDate.getMonth());

  // بناء خلايا التقويم
  const calendarCells = [];
  
  // إضافة الأيام الفارغة في بداية الشهر
  for (let i = 0; i < firstDay; i++) {
    calendarCells.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
  }

  const isToday = (day: number) => {
    const today = new Date();
    return day === today.getDate() && 
           currentDate.getMonth() === today.getMonth() && 
           currentDate.getFullYear() === today.getFullYear();
  };

  // إضافة أيام الشهر
  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    const dayBookings = bookings.filter(b => {
      const bDate = new Date(b.eventDate);
      return bDate.getFullYear() === currentDate.getFullYear() &&
             bDate.getMonth() === currentDate.getMonth() &&
             bDate.getDate() === day;
    });

    calendarCells.push(
      <div key={`day-${day}`} className={`calendar-day ${isToday(day) ? 'today' : ''}`}>
        <span className="day-number">{day}</span>
        {dayBookings.map(booking => (
          <div 
            key={booking._id || booking.id} 
            className={`booking-chip ${booking.bookingType}`}
            onClick={() => navigate(`/admin/bookings-management?search=${booking.bookingCode}`)}
            title={`عميل: ${booking.customerName} - وقت: ${booking.eventTime || 'غير محدد'}`}
          >
            <span className="chip-time">{booking.eventTime || '??:??'}</span>
            <span className="chip-name">{booking.customerName}</span>
          </div>
        ))}
      </div>
    );
  }

  const monthNames = ["يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو", "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"];
  const dayNames = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

  return (
    <AdminLayout title="جدول الحجوزات">
      <motion.div 
        className="admin-calendar-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>جاري تحميل الحجوزات...</p>
          </div>
        ) : (
          <>
            {/* بطاقة الحجز القادم */}
            <div className="upcoming-booking-section">
              {upcomingBooking ? (
                <div className="upcoming-booking-card">
                  <h3>🌟 أقرب حجز قادم</h3>
                  <div className="upcoming-details">
                    <div className="upcoming-detail-item">
                      <strong>العميل:</strong> {upcomingBooking.customerName}
                    </div>
                    <div className="upcoming-detail-item">
                      <strong>التاريخ:</strong> {new Date(upcomingBooking.eventDate).toLocaleDateString('en-GB')}
                    </div>
                    <div className="upcoming-detail-item">
                      <strong>الوقت:</strong> {upcomingBooking.eventTime}
                    </div>
                    <div className="upcoming-detail-item">
                      <strong>الموقع:</strong> {upcomingBooking.location}
                    </div>
                    <div className="upcoming-detail-item">
                      <strong>رقم الحجز:</strong> #{upcomingBooking.bookingCode}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-upcoming">
                  لا توجد أي حجوزات قادمة حالياً.
                </div>
              )}
            </div>

            {/* التقويم */}
            <div className="calendar-wrapper">
              <div className="calendar-header">
                <button className="month-nav-btn" onClick={prevMonth}>الشهر السابق</button>
                <h2>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
                <button className="month-nav-btn" onClick={nextMonth}>الشهر التالي</button>
              </div>

              <div className="calendar-grid">
                {dayNames.map(dayName => (
                  <div key={dayName} className="calendar-day-header">
                    {dayName}
                  </div>
                ))}
                {calendarCells}
              </div>
            </div>
          </>
        )}
      </motion.div>
    </AdminLayout>
  );
}

export default AdminCalendar;
